import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Paper,
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Theme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { COLORS } from "../theme/theme-options";
import TableRowsIcon from "@mui/icons-material/TableRows";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useEffect } from "react";

export type ColumnDef<T extends { id: string }> = {
  id: string;
  header: React.ReactNode;
  width?: number | string;
  align?: "left" | "center" | "right";
  headerAlign?: "left" | "center" | "right";
  render: (row: T) => React.ReactNode;
};

type Props<T extends { id: string }> = {
  title: string;
  rows: T[];
  loading?: boolean;
  colSpan: number;
  columns: ColumnDef<T>[];
  subtitle?: string;
  selectable?: boolean;
  actions?: boolean;
  createItemDialog?: string;
  onRowClick?: (row: T) => void;
  onRowMenuClick?: (row: T, anchorEl: HTMLElement) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  paperSx?: SxProps<Theme>;
};

const TruncatedTextCell: React.FC<{ text: string }> = ({ text }) => {
  const textRef = React.useRef<HTMLSpanElement | null>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const textValue = text.trim();

  const checkOverflow = React.useCallback(() => {
    const node = textRef.current;
    if (!node) {
      return;
    }

    setIsOverflowing(node.scrollWidth > node.clientWidth);
  }, []);

  React.useEffect(() => {
    if (!textValue) {
      setIsOverflowing(false);
      return;
    }

    const node = textRef.current;
    if (!node) {
      return;
    }

    const raf = window.requestAnimationFrame(() => {
      checkOverflow();
    });
    checkOverflow();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(checkOverflow);
      observer.observe(node);
      return () => {
        window.cancelAnimationFrame(raf);
        observer.disconnect();
      };
    }

    window.addEventListener("resize", checkOverflow);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [checkOverflow, textValue]);

  const shouldShowTooltip = textValue.length > 28 || isOverflowing;

  const content = (
    <Typography
      ref={textRef}
      component="span"
      sx={{
        display: "block",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Tooltip
      title={shouldShowTooltip ? text : ""}
      placement="top"
      arrow
      disableHoverListener={!shouldShowTooltip}
    >
      <Box
        sx={{ width: "100%", minWidth: 0 }}
        onMouseEnter={checkOverflow}
      >
        {content}
      </Box>
    </Tooltip>
  );
};

export const DataTable = <T extends { id: string },>({
  rows,
  loading = false,
  columns,
  colSpan,
  selectable = true,
  actions = true,
  onRowClick,
  createItemDialog,
  onRowMenuClick,
  onSelectionChange,
  paperSx,
}: Props<T>) => {
  const BORDER = alpha(COLORS.grey[700], 0.55);
  const BG = alpha(COLORS.grey[900], 0.6);
  const TEXT_STRONG = COLORS.grey[100];
  const TEXT_MUTED = alpha(COLORS.grey[300], 0.75);
  const HOVER = alpha(COLORS.grey[700], 0.35);
  const ACTION_COL_WIDTH = 64;
  const ACTION_BG = alpha(COLORS.grey[900], 0.78);
  const ACTION_BG_HOVER = alpha(COLORS.grey[700], 0.45);
  const SKELETON_ROWS = 10;

  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(rows.map((r) => r.id));
      const next = new Set<string>();
      prev.forEach((id) => ids.has(id) && next.add(id));
      return next;
    });
  }, [rows]);

  useEffect(() => {
    if (!onSelectionChange) {return;}
    onSelectionChange(Array.from(selected));
  }, [onSelectionChange, selected]);

  const allSelected =
    selectable && rows.length > 0 && selected.size === rows.length;
  const someSelected =
    selectable && selected.size > 0 && selected.size < rows.length;

  const toggleAll = (checked: boolean) => {
    if (!selectable) {return;}
    if (checked) {setSelected(new Set(rows.map((r) => r.id)));}
    else {setSelected(new Set());}
  };

  const toggleOne = (id: string) => {
    if (!selectable) {return;}
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {next.delete(id);}
      else {next.add(id);}
      return next;
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        overflow: "hidden",
        p: 0,
        ...paperSx,
      }}
    >

      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                borderBottom: `1px solid ${BORDER}`,
                backgroundColor: alpha(COLORS.grey[900], 0.35),
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: alpha(COLORS.grey[400], 0.9),
                py: 1,
              },
              "& th.actions-col": {
                position: "sticky",
                right: 0,
                zIndex: 4,
                width: ACTION_COL_WIDTH,
                minWidth: ACTION_COL_WIDTH,
                maxWidth: ACTION_COL_WIDTH,
                backgroundColor: ACTION_BG,
              },
            }}
          >
            {selectable && (
              <TableCell padding="checkbox" sx={{ width: 44 }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => toggleAll(e.target.checked)}
                  sx={{ p: 0.5 }}
                />
              </TableCell>
            )}

            {columns.map((c) => (
              <TableCell
                key={c.id}
                align={c.headerAlign ?? c.align ?? "left"}
                sx={{ width: c.width }}
              >
                {c.header}
              </TableCell>
            ))}

            {actions && <TableCell className="actions-col" sx={{ width: ACTION_COL_WIDTH }} />}
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            "& td": {
              borderBottom: `1px solid ${BORDER}`,
              backgroundColor: "transparent",
              color: TEXT_STRONG,
              fontSize: 14,
            },
            "& td.actions-col": {
              position: "sticky",
              right: 0,
              zIndex: 3,
              width: ACTION_COL_WIDTH,
              minWidth: ACTION_COL_WIDTH,
              maxWidth: ACTION_COL_WIDTH,
              backgroundColor: ACTION_BG,
              overflow: "visible",
              wordBreak: "normal",
            },
            "& tr:last-of-type td": { borderBottom: "none" },
          }}
        >
          {loading && Array.from({ length: SKELETON_ROWS }).map((_, idx) => (
            <TableRow key={`skeleton-row-${idx}`}>
              {selectable && (
                <TableCell padding="checkbox" sx={{ width: 44 }}>
                  <Skeleton variant="rounded" width={20} height={20} />
                </TableCell>
              )}

              {columns.map((c) => (
                <TableCell key={`skeleton-cell-${idx}-${c.id}`} align={c.align ?? "left"}>
                  <Skeleton variant="rounded" width="92%" height={20} />
                </TableCell>
              ))}

              {actions && (
                <TableCell className="actions-col" align="right">
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="circular" width={24} height={24} />
                  </Box>
                </TableCell>
              )}
            </TableRow>
          ))}
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={colSpan}
                sx={{
                  backgroundColor: "transparent",
                  borderBottom: "none",
                  p: 0,
                }}
              >
                <Box
                  sx={{
                    py: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 1,
                    color: TEXT_MUTED,
                  }}
                >
                  <TableRowsIcon
                    sx={{ fontSize: 72, color: alpha(TEXT_MUTED, 0.6) }}
                  />
                  <Typography
                    sx={{ fontSize: 18, fontWeight: 800, color: TEXT_STRONG }}
                  >
                    No data
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: TEXT_MUTED }}>
                    There are no items to display.
                  </Typography>
                  {createItemDialog && (
                    <Button
                      href={createItemDialog}
                      variant="outlined"
                      startIcon={<FontAwesomeIcon icon={faPlus} />}
                    >
                      New
                    </Button>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          )}
          {!loading && rows.map((row) => {
            const checked = selectable && selected.has(row.id);

            return (
              <TableRow
                key={row.id}
                hover
                onClick={() => onRowClick?.(row)}
                sx={{
                  cursor: onRowClick ? "pointer" : "default",
                  "&:hover td": { backgroundColor: HOVER },
                  "&:hover td.actions-col": { backgroundColor: ACTION_BG_HOVER },
                }}
              >
                {selectable && (
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ width: 44 }}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleOne(row.id)}
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>
                )}

                {columns.map((c) => (
                  <TableCell key={c.id} align={c.align ?? "left"} sx={{ minWidth: 0 }}>
                    {(() => {
                      const rendered = c.render(row);
                      const isTextValue =
                        typeof rendered === "string" || typeof rendered === "number";

                      if (isTextValue) {
                        const text = String(rendered);
                        return <TruncatedTextCell text={text} />;
                      }

                      return <Box sx={{ width: "100%", minWidth: 0 }}>{rendered}</Box>;
                    })()}
                  </TableCell>
                ))}

                {actions && (
                  <TableCell
                    className="actions-col"
                    align="right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        onRowMenuClick?.(row, e.currentTarget);
                      }}
                      aria-label="row actions"
                      sx={{
                        color: alpha(COLORS.grey[300], 0.85),
                        "&:hover": {
                          backgroundColor: alpha(COLORS.grey[800], 0.35),
                          color: COLORS.grey[100],
                        },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};
